export const SIGNATURE_MAX_FILE_SIZE = 2 * 1024 * 1024;
export const SIGNATURE_MIN_SOURCE_WIDTH = 400;
export const SIGNATURE_MIN_SOURCE_HEIGHT = 100;
export const SIGNATURE_OUTPUT_WIDTH = 500;
export const SIGNATURE_OUTPUT_HEIGHT = 200;
export const SIGNATURE_MIN_ASPECT_RATIO = 2;
export const SIGNATURE_MAX_ASPECT_RATIO = 4.5;

export interface SignatureCropLayout {
  width: number;
  height: number;
  left: number;
  top: number;
}

export interface SignatureCropBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface SignatureCropSource {
  dataUrl: string;
  width: number;
  height: number;
  image: HTMLImageElement;
}

export interface SignatureAutoCrop {
  zoom: number;
  offsetX: number;
  offsetY: number;
}

export function validateSignatureFile(file: File): string | null {
  if (!file.type.startsWith('image/')) {
    return 'Upload an image file containing the signature.';
  }

  if (file.size > SIGNATURE_MAX_FILE_SIZE) {
    return 'Signature uploads must be 2MB or smaller.';
  }

  return null;
}

export function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load the selected signature image.'));
    };

    image.src = objectUrl;
  });
}

export function loadImageFromDataUrl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load the selected signature image.'));
    image.src = src;
  });
}

export function readFileAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Failed to read the selected signature file.'));
    };
    reader.onerror = () => reject(new Error('Failed to read the selected signature file.'));
    reader.readAsDataURL(file);
  });
}

export async function extractSignatureCropSource(file: File): Promise<SignatureCropSource | string> {
  const image = await loadImageFromFile(file);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;

  if (width < SIGNATURE_MIN_SOURCE_WIDTH || height < SIGNATURE_MIN_SOURCE_HEIGHT) {
    return `Signature image must be at least ${SIGNATURE_MIN_SOURCE_WIDTH}x${SIGNATURE_MIN_SOURCE_HEIGHT}px before cropping.`;
  }

  return {
    dataUrl: await readFileAsDataUrl(file),
    width,
    height,
    image,
  };
}

export function detectSignatureBounds(image: HTMLImageElement): SignatureCropBounds | null {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return null;
  }

  context.drawImage(image, 0, 0);
  const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const alpha = data[index + 3];
      const red = data[index];
      const green = data[index + 1];
      const blue = data[index + 2];
      const brightness = (red + green + blue) / 3;
      const isTransparent = alpha < 20;
      const isNearWhite = alpha > 20 && red > 245 && green > 245 && blue > 245;

      if (!isTransparent && !isNearWhite && brightness < 235) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (maxX < 0 || maxY < 0) {
    return null;
  }

  return { left: minX, top: minY, right: maxX, bottom: maxY };
}

export function computeAutoSignatureCrop(
  image: HTMLImageElement,
  viewportWidth: number,
  viewportHeight: number,
): SignatureAutoCrop {
  const bounds = detectSignatureBounds(image);

  if (!bounds || isSignatureBoundsUnreliable(bounds, image.width, image.height)) {
    return { zoom: 1, offsetX: 0, offsetY: 0 };
  }

  const width = Math.max(1, bounds.right - bounds.left);
  const height = Math.max(1, bounds.bottom - bounds.top);
  const viewportRatio = viewportWidth / viewportHeight;
  const targetWidth = width * 1.25;
  const targetHeight = height * 1.6;
  const fittedWidth = Math.max(targetWidth, targetHeight * viewportRatio);
  const baseScale = Math.max(viewportWidth / image.width, viewportHeight / image.height);
  const zoom = clamp(viewportWidth / (baseScale * fittedWidth), 1, 3);

  const scaledWidth = image.width * baseScale * zoom;
  const scaledHeight = image.height * baseScale * zoom;
  const bboxCenterX = (bounds.left + bounds.right) / 2;
  const bboxCenterY = (bounds.top + bounds.bottom) / 2;
  const deltaX = (bboxCenterX - image.width / 2) * baseScale * zoom;
  const deltaY = (bboxCenterY - image.height / 2) * baseScale * zoom;
  const extraWidth = Math.max(0, scaledWidth - viewportWidth);
  const extraHeight = Math.max(0, scaledHeight - viewportHeight);

  const offsetX = extraWidth > 0 ? clamp((-2 * deltaX * 100) / extraWidth, -100, 100) : 0;
  const offsetY = extraHeight > 0 ? clamp((-2 * deltaY * 100) / extraHeight, -100, 100) : 0;

  return {
    zoom: Math.round(zoom * 10) / 10,
    offsetX: Math.round(offsetX),
    offsetY: Math.round(offsetY),
  };
}

function isSignatureBoundsUnreliable(
  bounds: SignatureCropBounds,
  imageWidth: number,
  imageHeight: number,
): boolean {
  const width = bounds.right - bounds.left;
  const height = bounds.bottom - bounds.top;

  return width > imageWidth * 0.92 && height > imageHeight * 0.92;
}

export function getSignatureCropLayout(
  imageWidth: number,
  imageHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  zoom: number,
  offsetX: number,
  offsetY: number,
): SignatureCropLayout {
  if (!imageWidth || !imageHeight) {
    return { width: viewportWidth, height: viewportHeight, left: 0, top: 0 };
  }

  const baseScale = Math.max(viewportWidth / imageWidth, viewportHeight / imageHeight);
  const scaledWidth = imageWidth * baseScale * zoom;
  const scaledHeight = imageHeight * baseScale * zoom;
  const extraWidth = Math.max(0, scaledWidth - viewportWidth);
  const extraHeight = Math.max(0, scaledHeight - viewportHeight);
  const left = (viewportWidth - scaledWidth) / 2 + (extraWidth / 2) * (offsetX / 100);
  const top = (viewportHeight - scaledHeight) / 2 + (extraHeight / 2) * (offsetY / 100);

  return { width: scaledWidth, height: scaledHeight, left, top };
}

export function renderSignatureCrop(
  image: HTMLImageElement,
  zoom: number,
  offsetX: number,
  offsetY: number,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = SIGNATURE_OUTPUT_WIDTH;
  canvas.height = SIGNATURE_OUTPUT_HEIGHT;
  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    throw new Error('Unable to prepare the cropped signature. Please try again.');
  }

  const layout = getSignatureCropLayout(
    image.width,
    image.height,
    SIGNATURE_OUTPUT_WIDTH,
    SIGNATURE_OUTPUT_HEIGHT,
    zoom,
    offsetX,
    offsetY,
  );

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, layout.left, layout.top, layout.width, layout.height);
  cleanSignatureCanvas(context, canvas.width, canvas.height);

  return canvas;
}

export function validateSignatureCanvas(canvas: HTMLCanvasElement): string | null {
  const aspectRatio = canvas.width / canvas.height;

  if (aspectRatio < SIGNATURE_MIN_ASPECT_RATIO || aspectRatio > SIGNATURE_MAX_ASPECT_RATIO) {
    return 'Signature crop must be wide and short, like a real signature.';
  }

  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return 'Unable to validate signature image. Please try another PNG file.';
  }

  const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);
  const sampleStep = Math.max(1, Math.floor(Math.min(width, height) / 120));

  let sampledPixels = 0;
  let nonBackgroundPixels = 0;
  let darkStrokePixels = 0;
  let coloredPixels = 0;

  for (let y = 0; y < height; y += sampleStep) {
    for (let x = 0; x < width; x += sampleStep) {
      const index = (y * width + x) * 4;
      const red = data[index];
      const green = data[index + 1];
      const blue = data[index + 2];
      const alpha = data[index + 3];

      sampledPixels += 1;

      const maxRgb = Math.max(red, green, blue);
      const minRgb = Math.min(red, green, blue);
      const brightness = (red + green + blue) / 3;
      const saturation = maxRgb === 0 ? 0 : (maxRgb - minRgb) / maxRgb;
      const isTransparent = alpha < 20;
      const isNearWhite = alpha > 20 && red > 245 && green > 245 && blue > 245;
      const isBackground = isTransparent || isNearWhite;

      if (!isBackground) {
        nonBackgroundPixels += 1;

        if (brightness < 200) {
          darkStrokePixels += 1;
        }

        if (saturation > 0.25 && brightness > 40 && brightness < 245) {
          coloredPixels += 1;
        }
      }
    }
  }

  if (sampledPixels === 0) {
    return 'Unable to validate signature image. Please try another PNG file.';
  }

  const nonBackgroundRatio = nonBackgroundPixels / sampledPixels;
  const darkStrokeRatio = darkStrokePixels / sampledPixels;
  const coloredRatio = coloredPixels / sampledPixels;

  if (nonBackgroundRatio < 0.005) {
    return 'Signature image appears blank. Please upload a clearer signature.';
  }

  if (nonBackgroundRatio > 0.35) {
    return 'Signature image should contain only the signature on a plain background, not a full photo.';
  }

  if (darkStrokeRatio < 0.003) {
    return 'Signature image is too faint. Use a darker signature on a plain white or transparent background.';
  }

  if (coloredRatio > 0.1) {
    return 'Signature image should not be a colorful picture. Upload only the signature on a plain background.';
  }

  return null;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function canvasToBase64Png(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL('image/png').split('base64,')[1] || '';
}

function cleanSignatureCanvas(context: CanvasRenderingContext2D, width: number, height: number): void {
  const imageData = context.getImageData(0, 0, width, height);
  const { data } = imageData;
  const pixelCount = width * height;
  const luminance = new Float32Array(pixelCount);
  const integral = new Float32Array((width + 1) * (height + 1));
  const radius = 5;

  for (let y = 0; y < height; y += 1) {
    let rowSum = 0;
    for (let x = 0; x < width; x += 1) {
      const pixelIndex = y * width + x;
      const index = pixelIndex * 4;
      const red = data[index];
      const green = data[index + 1];
      const blue = data[index + 2];
      const alpha = data[index + 3];
      const value = alpha < 20 ? 255 : 0.299 * red + 0.587 * green + 0.114 * blue;

      luminance[pixelIndex] = value;
      rowSum += value;
      integral[(y + 1) * (width + 1) + (x + 1)] = integral[y * (width + 1) + (x + 1)] + rowSum;
    }
  }

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const pixelIndex = y * width + x;
      const index = pixelIndex * 4;
      const red = data[index];
      const green = data[index + 1];
      const blue = data[index + 2];
      const alpha = data[index + 3];
      const maxRgb = Math.max(red, green, blue);
      const minRgb = Math.min(red, green, blue);
      const saturation = maxRgb === 0 ? 0 : (maxRgb - minRgb) / maxRgb;
      const currentLuminance = luminance[pixelIndex];
      const isTransparent = alpha < 20;

      const left = Math.max(0, x - radius);
      const top = Math.max(0, y - radius);
      const right = Math.min(width - 1, x + radius);
      const bottom = Math.min(height - 1, y + radius);
      const area = (right - left + 1) * (bottom - top + 1);
      const sum =
        integral[(bottom + 1) * (width + 1) + (right + 1)] -
        integral[top * (width + 1) + (right + 1)] -
        integral[(bottom + 1) * (width + 1) + left] +
        integral[top * (width + 1) + left];
      const localAverage = sum / area;
      const localContrast = localAverage - currentLuminance;
      const isColoredInk = saturation > 0.18 && currentLuminance < 230;
      const isDarkStroke = currentLuminance < 170 && localContrast > 12;
      const isMidStroke = currentLuminance < 210 && localContrast > 22;
      const isStroke = !isTransparent && (isColoredInk || isDarkStroke || isMidStroke);

      if (!isStroke) {
        data[index] = 255;
        data[index + 1] = 255;
        data[index + 2] = 255;
        data[index + 3] = 255;
        continue;
      }

      const normalizedInk = Math.max(
        0,
        Math.min(255, Math.round(currentLuminance * 0.35 - Math.min(35, localContrast * 0.6) - saturation * 18)),
      );
      data[index] = normalizedInk;
      data[index + 1] = normalizedInk;
      data[index + 2] = normalizedInk;
      data[index + 3] = 255;
    }
  }

  context.putImageData(imageData, 0, 0);
}
